import React, { useEffect, useState } from 'react';
import {
  TextField,
  FormHelperText,
  IconButton,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

export interface TeamMember {
  username: string;
  role: 'maintainer' | 'member';
}

export const TeamMembersSelector = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<TeamMember[]>) => {
  const catalogApi = useApi(catalogApiRef);
  const [users, setUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<TeamMember[]>(formData || []);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<'maintainer' | 'member'>(
    'member',
  );

  const title = schema.title || 'Team Members';

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Get all User entities from catalog
        const { items: userEntities } = await catalogApi.getEntities({
          filter: { kind: 'User', 'metadata.tags': 'github-org' },
        });

        // Extract usernames from metadata.name
        const usernames = userEntities
          .map(user => user.metadata.name)
          .filter(Boolean)
          .sort();

        setUsers(usernames);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [catalogApi]);

  useEffect(() => {
    onChange(members);
  }, [members, onChange]);

  const handleAddMember = () => {
    if (selectedUser && !members.find(m => m.username === selectedUser)) {
      const newMembers = [
        ...members,
        { username: selectedUser, role: selectedRole },
      ];
      setMembers(newMembers);
      setSelectedUser('');
      setSelectedRole('member');
    }
  };

  const handleRemoveMember = (username: string) => {
    const newMembers = members.filter(m => m.username !== username);
    setMembers(newMembers);
  };

  const handleRoleChange = (
    username: string,
    role: 'maintainer' | 'member',
  ) => {
    const newMembers = members.map(m =>
      m.username === username ? { ...m, role } : m,
    );
    setMembers(newMembers);
  };

  return (
    <Box>
      <Typography
        variant="subtitle1"
        style={{ marginTop: 16, marginBottom: 8 }}
      >
        {title}
      </Typography>
      <Box display="flex" alignItems="flex-start" style={{ gap: 16 }}>
        <Box flex={1}>
          <Autocomplete
            options={users.filter(u => !members.find(m => m.username === u))}
            value={selectedUser || null}
            onChange={(_, newValue) => setSelectedUser(newValue || '')}
            disabled={loading}
            renderInput={params => (
              <TextField
                {...params}
                label="Select Github Username"
                margin="normal"
                fullWidth
                helperText={
                  loading
                    ? 'Loading users...'
                    : users.length === 0
                    ? 'No users found in catalog'
                    : 'Select a user to add to the team'
                }
              />
            )}
          />
        </Box>
        <FormControl margin="normal" style={{ minWidth: 120 }}>
          <InputLabel>Role</InputLabel>
          <Select
            value={selectedRole}
            onChange={e =>
              setSelectedRole(e.target.value as 'maintainer' | 'member')
            }
          >
            <MenuItem value="member">Member</MenuItem>
            <MenuItem value="maintainer">Maintainer</MenuItem>
          </Select>
        </FormControl>
        <Box marginTop={2}>
          <IconButton
            color="primary"
            onClick={handleAddMember}
            disabled={!selectedUser}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      {members.length > 0 && (
        <Box marginTop={2}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Members ({members.length})
          </Typography>
          {members.map(member => (
            <Box
              key={member.username}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom={1}
              padding={1}
              border="1px solid #e0e0e0"
              borderRadius={4}
            >
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography>{member.username}</Typography>
                <Chip
                  label={member.role}
                  size="small"
                  color={member.role === 'maintainer' ? 'primary' : 'default'}
                />
              </Box>
              <Box display="flex" style={{ gap: 8 }}>
                <Select
                  value={member.role}
                  onChange={e =>
                    handleRoleChange(
                      member.username,
                      e.target.value as 'maintainer' | 'member',
                    )
                  }
                  style={{ minWidth: 120, marginRight: 8 }}
                >
                  <MenuItem value="member">Member</MenuItem>
                  <MenuItem value="maintainer">Maintainer</MenuItem>
                </Select>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveMember(member.username)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {rawErrors?.length > 0 && required && members.length === 0 && (
        <FormHelperText error>At least one member is required</FormHelperText>
      )}
    </Box>
  );
};
